import { parseAmountToCents } from "@talentkontor/domain";

/**
 * Betragseingabe → ganze Cent über den gemeinsamen Parser (parseAmountToCents): „1234,50“, „1234.50“, „1.234,50“,
 * „1,234.50“ und „1'234.50“ ergeben alle 123450. Das letzte Komma bzw. der letzte Punkt mit ein oder zwei Folgeziffern
 * gilt als Dezimaltrenner, alle übrigen Trenner als Tausendertrenner. Leer: null; nicht lesbar: NaN.
 * Der Wert ist schon in Cent und darf nicht noch einmal mit 100 multipliziert werden.
 */
export function parseCentsInput(text: string): number | null {
  const s = text.trim();
  if (!s) return null;
  try {
    return parseAmountToCents(s);
  } catch {
    return Number.NaN;
  }
}

/** Eingegebener Betrag als Text für das Eingabefeld (Vorbelegung), z. B. 123450 → „1234,50“. */
export function centsToInput(cents: number): string {
  return (cents / 100).toFixed(2).replace(".", ",");
}

/**
 * Plausibilität gegenüber dem erwarteten (offenen) Betrag: Übersteigt die Eingabe ihn um mehr als das Doppelte, ist
 * sehr wahrscheinlich ein Trenner falsch gelesen oder eine Null zu viel getippt. Dann erst nach Bestätigung buchen.
 */
export function farAbove(cents: number, expectedCents: number): boolean {
  return expectedCents > 0 && Number.isFinite(cents) && cents > expectedCents * 2;
}

/**
 * Mengeneingabe → Zahl (Gegenprüfung GB-4): „1“, „2,5“, „0.25“, „1,125“; höchstens `maxDecimals` Nachkommastellen (Rechnungen
 * speichern drei), keine Tausendertrenner, kein Vorzeichen. Leer: null; nicht lesbar: NaN (nie still 0). `integer`: nur ganze
 * Zahlen, dann ist auch „1.000“ unlesbar statt 1. Mehrdeutiges wie „1.000“ (ohne `integer` = 1) zeigt die Oberfläche als
 * erkannte Menge an.
 */
export function parseQuantityInput(text: string, opts: { maxDecimals?: number; integer?: boolean } = {}): number | null {
  const s = text.trim().replace(/[\s  ]/g, "");
  if (!s) return null;
  const max = opts.integer ? 0 : Math.max(0, Math.min(6, opts.maxDecimals ?? 3));
  const ok = max === 0 ? /^\d+$/.test(s) : new RegExp(`^(?:\\d+(?:[.,]\\d{1,${max}})?|[.,]\\d{1,${max}})$`).test(s);
  return ok ? Number(s.replace(",", ".")) : Number.NaN;
}

/** Menge für die Anzeige („1,5“, „1.5“ in de-CH/en-GB), höchstens drei Nachkommastellen. */
export function quantityText(q: number, decimal = ","): string {
  return String(Math.round(q * 1000) / 1000).replace(".", decimal);
}
